import type { SEOMetadata, SupportedLocale } from '../locales/base'
import { getHomePageContent } from '../content/homeContent'

/**
 * SEO Metadata 管理工具
 * 用於動態設定 HTML head 中的 meta 標籤
 */

export function setSEOMetadata(metadata: SEOMetadata, locale: SupportedLocale) {
  // 設定 title
  document.title = metadata.title

  // 設定或更新 meta 標籤
  updateMetaTag('description', metadata.description)
  updateMetaTag('keywords', metadata.keywords?.join(', ') || '')

  // Open Graph 標籤
  if (metadata.ogTitle) {
    updateMetaTag('og:title', metadata.ogTitle, 'property')
  }
  if (metadata.ogDescription) {
    updateMetaTag('og:description', metadata.ogDescription, 'property')
  }
  if (metadata.ogImage) {
    updateMetaTag('og:image', metadata.ogImage, 'property')
  }

  // Twitter Card
  if (metadata.twitterCard) {
    updateMetaTag('twitter:card', metadata.twitterCard)
  }

  // hreflang 標籤（用於多語系）
  updateHrefLangTags(locale)

  // Canonical 標籤
  updateCanonicalTag(locale)
}

function updateMetaTag(name: string, content: string, attribute: 'name' | 'property' = 'name') {
  if (!content) return

  let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement | null

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, name)
    document.head.appendChild(element)
  }

  element.content = content
}

function updateHrefLangTags(currentLocale: SupportedLocale) {
  // 移除舊的 hreflang 標籤
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove())

  // 建立新的 hreflang 標籤
  const locales: Array<{ code: SupportedLocale; hreflang: string; path: string }> = [
    { code: 'zh-TW', hreflang: 'zh-TW', path: '/' },
    { code: 'en', hreflang: 'en', path: '/en' },
    { code: 'ja', hreflang: 'ja', path: '/jp' }
  ]

  const currentPath = window.location.pathname.replace(/^\/(zh-tw|en|jp)/, '')

  for (const locale of locales) {
    const link = document.createElement('link')
    link.rel = 'alternate'
    link.hreflang = locale.hreflang
    link.href = `${window.location.origin}${locale.path}${currentPath}`
    document.head.appendChild(link)
  }

  // x-default 標籤
  const xDefaultLink = document.createElement('link')
  xDefaultLink.rel = 'alternate'
  xDefaultLink.hreflang = 'x-default'
  xDefaultLink.href = `${window.location.origin}${currentPath || '/'}`
  document.head.appendChild(xDefaultLink)
}

function updateCanonicalTag(locale: SupportedLocale) {
  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null

  if (!canonical) {
    canonical = document.createElement('link')
    canonical.rel = 'canonical'
    document.head.appendChild(canonical)
  }

  const currentPath = window.location.pathname.replace(/^\/(zh-tw|en|jp)/, '')
  const localePrefix = locale === 'zh-TW' ? '' : locale === 'en' ? '/en' : '/jp'

  canonical.href = `${window.location.origin}${localePrefix}${currentPath}`
}

/**
 * 為指南頁面生成 SEO metadata
 */
export function generateGuideMetadata(
  guideTitle: string,
  guideDescription: string,
  locale: SupportedLocale
): SEOMetadata {
  const localePrefix = locale === 'en' ? '' : locale === 'zh-TW' ? '繁體中文 | ' : '日本語 | '

  return {
    title: `${localePrefix}${guideTitle} - SrcRank`,
    description: guideDescription,
    keywords: ['SEO', 'optimization', 'guide', 'content'],
    ogTitle: guideTitle,
    ogDescription: guideDescription
  }
}

/**
 * 為首頁生成 SEO metadata
 */
export function generateHomeMetadata(locale: SupportedLocale): SEOMetadata {
  return getHomePageContent(locale).metadata as SEOMetadata
}
